import type { ActionPlanV2Row } from '../services/inventory-plan-clusters.service';
import type {
  PageIntentDiagnosis,
  PageRealizationSnapshot,
  PageSerpSnapshot
} from '../services/dataforseo';
import { formatInventoryRealizationRatio } from '../components/content-inventory/inventory-realization-sort';
import { findTopSerpRival } from '@taptimize/shared/dist/review/serp-rival';
import type { RankDrivenSectionGap } from './rank-driven-section-gaps';

export { findTopSerpRival };
export type { TopSerpRivalPick } from '@taptimize/shared/dist/review/serp-rival';

export interface PlanV2ExpandPlaybookStep {
  label: string;
  kind: 'quick_compare' | 'scroll';
  compareUrl?: string;
  scrollTargetId?: string;
}

export interface PlanV2ExpandPlaybookHeader {
  claimLabel: string;
  gapLabel: string;
  ratioLabel: string;
  kdLabel: string;
  ceilingDriverKeyword: string | null;
  queueRationale: string;
}

export interface PlanV2ExpandPlaybook {
  header: PlanV2ExpandPlaybookHeader;
  steps: PlanV2ExpandPlaybookStep[];
}

export interface BuildPlanV2ExpandPlaybookInput {
  row: ActionPlanV2Row;
  pageRealization: PageRealizationSnapshot | null;
  pageSerp: PageSerpSnapshot | null;
  pageIntentDiagnosis: PageIntentDiagnosis | null;
  freshnessShowsWarn: boolean;
  selfDomain: string;
  queueRank?: number | null;
  /** True when precomputed section-gap eval exists for the current top SERP rival. */
  serpRivalSectionGapsReady?: boolean;
  /** FW-URL-10: top rank-driven gaps from Top SERP rival eval. */
  rankDrivenGaps?: RankDrivenSectionGap[];
}

function formatGap(gap: number | null | undefined): string {
  if (gap === null || gap === undefined || !Number.isFinite(gap)) return '—';
  return String(Math.round(gap));
}

function formatKd(kd: number | null | undefined): string {
  if (kd === null || kd === undefined || !Number.isFinite(kd)) return '—';
  return String(Math.round(kd));
}

function kdUpsideLabel(kd: number | null | undefined): string {
  if (kd === null || kd === undefined || !Number.isFinite(kd)) return 'unknown SERP difficulty';
  const n = Number(kd);
  if (n <= 30) return 'easy SERP';
  if (n <= 55) return 'moderate SERP difficulty';
  return 'competitive SERP';
}

function gapUpsideLabel(gap: number | null | undefined): string {
  if (gap === null || gap === undefined || !Number.isFinite(gap)) return 'limited scored upside';
  const n = Number(gap);
  if (n >= 300) return 'high upside';
  if (n >= 80) return 'material upside';
  return 'modest upside';
}

function buildQueueRationale(
  row: ActionPlanV2Row,
  queueRank?: number | null
): string {
  const gap = row.metrics.realizationGap;
  const ratio = row.metrics.realizationRatio;
  const kd = row.serpKeywordDifficulty;
  const rankPrefix =
    queueRank !== null && queueRank !== undefined && queueRank > 0
      ? `#${queueRank} in queue — `
      : '';
  const gapText = formatGap(gap);
  const ratioText = formatInventoryRealizationRatio(ratio);
  const kdText = formatKd(kd);
  const upside = gapUpsideLabel(gap);
  const kdPhrase = kdUpsideLabel(kd);
  const guess = `${rankPrefix}Gap ${gapText}, ratio ${ratioText}, KD ${kdText} — ${upside}, ${kdPhrase}.`;

  // LB5: gap/ratio/KD is a forecast. A measured move since the previous crawl is
  // not, and it is what actually put this row where it is — so it leads.
  const change = row.periodChange;
  if (!change) return guess;
  return `${change.evidence}. ${guess}`;
}


export function buildPlanV2ExpandPlaybook(
  input: BuildPlanV2ExpandPlaybookInput
): PlanV2ExpandPlaybook | null {
  if (input.row.recommendedAction.type !== 'prioritize') return null;

  const claimLabel = input.row.metrics.demandClaim?.trim() || '—';
  const ceilingDriverKeyword =
    input.pageRealization?.ceilingDriver?.keyword?.trim() || null;

  const header: PlanV2ExpandPlaybookHeader = {
    claimLabel,
    gapLabel: formatGap(input.row.metrics.realizationGap),
    ratioLabel: formatInventoryRealizationRatio(input.row.metrics.realizationRatio),
    kdLabel: formatKd(input.row.serpKeywordDifficulty),
    ceilingDriverKeyword,
    queueRationale: buildQueueRationale(input.row, input.queueRank)
  };

  const steps: PlanV2ExpandPlaybookStep[] = [];
  const rival = findTopSerpRival(input.pageSerp, input.selfDomain);
  if (rival) {
    const gapsSuffix =
      input.serpRivalSectionGapsReady ? ' — review section gaps.' : '.';
    const skipSuffix = rival.skippedNote ? ` (${rival.skippedNote})` : '';
    steps.push({
      label: `Quick Compare vs ${rival.domain}${skipSuffix}${gapsSuffix}`,
      kind: 'quick_compare',
      compareUrl: rival.url
    });
  }

  const topRankGap = input.rankDrivenGaps?.[0];
  if (topRankGap) {
    const vol =
      topRankGap.searchVolume > 0
        ? ` (${topRankGap.searchVolume.toLocaleString('en-US')}/mo)`
        : '';
    const focalPart = topRankGap.focalPassage
      ? ` On your page: "${topRankGap.focalPassage}"`
      : ' No matching passage on your page.';
    const rivalPart = topRankGap.rivalPassage
      ? ` Their page: "${topRankGap.rivalPassage}"`
      : '';
    steps.push({
      label: `Keyword "${topRankGap.keyword}" — they rank #${topRankGap.rivalPosition}${vol}.${rivalPart}${focalPart}`,
      kind: 'scroll',
      scrollTargetId: 'url-topic-ownership-heading'
    });
  }

  const diagnosis = input.pageIntentDiagnosis;
  // Never ask operators to "align to mixed" — that is not an actionable format.
  if (
    diagnosis &&
    !diagnosis.intentAlignedWithSerp &&
    diagnosis.serpFamily !== 'unknown' &&
    diagnosis.serpFamily !== 'mixed'
  ) {
    steps.push({
      label: `Align page format with SERP (${diagnosis.serpFamily}).`,
      kind: 'scroll',
      scrollTargetId: 'url-serp-heading'
    });
  }

  if (input.freshnessShowsWarn) {
    steps.push({
      label: 'Refresh content vs SERP winners.',
      kind: 'scroll',
      scrollTargetId: 'url-freshness-heading'
    });
  }

  if (steps.length === 0) {
    steps.push({
      label: 'Review SERP winners and demand ceiling keyword.',
      kind: 'scroll',
      scrollTargetId: 'url-serp-heading'
    });
  }

  return {
    header,
    steps: steps.slice(0, 3)
  };
}
